import { onBeforeMount, onUpdated, ref, watch } from 'vue'
import FormCheckbox from '../FormCheckbox/FormCheckbox.vue'
import FormInputDropdown from '../FormInputDropdown/FormInputDropdown.vue'
import FormInputTextField from '../FormInputTextField/FormInputTextField.vue'
import FormInputTextarea from '../FormInputTextarea/FormInputTextarea.vue'
import nonEmptyStringValidator from 'foundation/js/inputValidators/nonEmptyStringValidator'

export default {
    name: 'StafetFlowLightBagsMetaList',
    components: {
        FormCheckbox,
        FormInputDropdown,
        FormInputTextField,
        FormInputTextarea
    },
    props: {
        labels: {
            type: Object,
            required: true
        },
        numberOfDonations: {
            type: Number,
            required: true
        },
        onChangeCallback: {
            type: Function,
            required: true
        },
        displayInputError: {
            type: Boolean,
            required: false,
            default: false
        },
        data: {
            type: Object,
            required: false
        },
        isLysposerDedicated: {
            type: Boolean,
            required: false,
            default: false
        },
        addPersonalNote: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const isLysposerDedicated = ref(props.isLysposerDedicated)
        const addPersonalNote = ref(props.addPersonalNote)

        const dedicatedTypeAll = ref('')
        const lysposeDedicatedToAll = ref('')
        const lightPersonalNoteAll = ref('')

        const donationAmountList = ref([])

        // static options
        const dedicationTypeOptions = [
            {
                name: props.labels.inMemoryOf,
                value: 'TilMindeFor'
            },
            {
                name: props.labels.inSupportOf,
                value: 'TilStoetteFor'
            }
        ]

        watch(
            () => props.numberOfDonations,
            (newValue, oldValue) => {
                if (newValue < oldValue) {
                    donationAmountList.value = donationAmountList.value.slice(
                        0,
                        newValue
                    )
                } else {
                    const diff = newValue - oldValue
                    for (let i = 0; i < diff; i++) {
                        donationAmountList.value.push({
                            lysposeDedicatedTo: '',
                            personalNote: '',
                            lysposeDedicatedType: ''
                        })
                    }
                }
            }
        )

        onBeforeMount(() => {
            if (props.isLysposerDedicated) {
                dedicatedTypeAll.value = props.data[0].lysposeDedicatedType
                lysposeDedicatedToAll.value = props.data[0].lysposeDedicatedTo
                lightPersonalNoteAll.value = props.data[0].personalNote
            }

            if (props.data?.length > 0) {
                donationAmountList.value = props.data
            } else {
                for (let i = 0; i < props.numberOfDonations; i++) {
                    donationAmountList.value.push({
                        lysposeDedicatedTo: '',
                        personalNote: '',
                        lysposeDedicatedType: ''
                    })
                }
            }

            if (donationAmountList.value.length < props.numberOfDonations) {
                const diff =
                    props.numberOfDonations - donationAmountList.value.length
                for (let i = 0; i < diff; i++) {
                    donationAmountList.value.push({
                        lysposeDedicatedTo: '',
                        personalNote: '',
                        lysposeDedicatedType: ''
                    })
                }
            } else {
                donationAmountList.value = donationAmountList.value.slice(
                    0,
                    props.numberOfDonations
                )
            }

            handleCallbackData()
        })

        onUpdated(() => {
            handleCallbackData()
        })

        const validateDonationItems = () => {

            const dedicatedTypeValid = isLysposerDedicated.value
                ? nonEmptyStringValidator(dedicatedTypeAll.value)
                : donationAmountList.value.every((item) => nonEmptyStringValidator(item.lysposeDedicatedType))

            const lightDedicatedToValid = isLysposerDedicated.value
                ? nonEmptyStringValidator(lysposeDedicatedToAll.value)
                : donationAmountList.value.every((item) => nonEmptyStringValidator(item.lysposeDedicatedTo))

            return dedicatedTypeValid && lightDedicatedToValid
        }

        const handleCallbackData = () => {
            if (props.onChangeCallback) {
                props.onChangeCallback({
                    isLysposerDedicated: isLysposerDedicated.value,
                    addPersonalNote: addPersonalNote.value,
                    donationItems: donationAmountList.value,
                    isValid: validateDonationItems()
                })
            }
        }

        const toggleAddPersonalNote = (value) => {
            addPersonalNote.value = value
        }

        const toggleDedicatedLightBag = (value) => {
            isLysposerDedicated.value = value

            if (value) {
                dedicatedTypeAll.value = donationAmountList.value[0].lysposeDedicatedType
                lysposeDedicatedToAll.value = donationAmountList.value[0].lysposeDedicatedTo
                lightPersonalNoteAll.value = donationAmountList.value[0].personalNote
            }
            
            donationAmountList.value.forEach((item) => {
                item.lysposeDedicatedType = dedicatedTypeAll.value
                item.lysposeDedicatedTo = lysposeDedicatedToAll.value
                item.personalNote = lightPersonalNoteAll.value
            })
        }

        const updateLightDedicatedTo = (index, value) => {
            donationAmountList.value[index].lysposeDedicatedTo = value
            handleCallbackData()
        }

        const updatePersonalNote = (index, value) => {
            donationAmountList.value[index].personalNote = value
            handleCallbackData()
        }

        const updateDedicationType = (index, value) => {
            donationAmountList.value[index].lysposeDedicatedType = value
            handleCallbackData()
        }

        const setDedicationTypeAll = (value) => {
            donationAmountList.value.forEach((item) => {
                item.lysposeDedicatedType = value
            })
            dedicatedTypeAll.value = value
            handleCallbackData()
        }

        const setLightDedicatedToAll = (value) => {
            donationAmountList.value.forEach((item) => {
                item.lysposeDedicatedTo = value
            })
            lysposeDedicatedToAll.value = value
            handleCallbackData()
        }

        const setPersonalNoteAll = (value) => {
            donationAmountList.value.forEach((item) => {
                item.personalNote = value
            })
            lightPersonalNoteAll.value = value
            handleCallbackData()
        }

        return {
            isLysposerDedicated,
            addPersonalNote,
            donationAmountList,
            dedicationTypeOptions,
            dedicatedTypeAll,
            lysposeDedicatedToAll,
            lightPersonalNoteAll,
            setDedicationTypeAll,
            setLightDedicatedToAll,
            setPersonalNoteAll,
            toggleAddPersonalNote,
            toggleDedicatedLightBag,
            updateLightDedicatedTo,
            updatePersonalNote,
            updateDedicationType
        }
    }
}
